import {Component, computed, effect, viewChildren} from '@angular/core';
import {DynamicFormControlComponent, IFormGroup, TAnyFormElement} from 'frontier';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {
  FormElementComponent
} from 'frontier/browserkit/src/lib/components/control/form-control/dynamic-form/form-element/form-element.component';
import {merge} from 'rxjs';

@Component({
  selector: 'lib-custom-dynamic-form',
  standalone: true,
  template: '',
  imports: [],
})
export abstract class CustomDynamicFormComponent extends DynamicFormControlComponent {
  formElements = viewChildren(FormElementComponent);

  protected controlFormObjects: { [key: string]: TAnyFormElement } = {};

  // All form elements in a flattened form. Key is the form element key and value is the form element object
  protected flattenedFormElements = computed<{ [key: string]: TAnyFormElement }>(() => {
    return this.data()?.formGroups?.reduce((acc, currentValue) => {
      currentValue.elements.forEach((element) => {
        acc[element.key] = element;
      });
      return acc;
    }, {} as { [key: string]: TAnyFormElement });
  });

  // The root form group object that holds all form elements
  form = computed(() => {
    if (!this.data()?.formGroups) {
      return null;
    }
    const group: { [key: string]: FormControl } = {};
    this.data().formGroups.forEach((fg: IFormGroup) => {
      fg.elements.forEach((baseElement: TAnyFormElement) => {

        // save reference for remap
        this.controlFormObjects[baseElement.key] = baseElement;

        // create a form group json object
        group[baseElement.key] = new FormControl(baseElement.value != null ? baseElement.value : 0);

        if (baseElement.required) {
          group[baseElement.key].addValidators([Validators.required]);
        }
      });
    })
    return new FormGroup(group);
  });

  constructor() {
    super();
    effect(() => {
      const formElements = this.formElements();
      formElements.forEach((formElement) => {
        // subscribe to the focus out and request save event of the form element
        this.subs.add(
          merge(
            formElement.focusOutEvent,
            formElement.requestSave
          ).subscribe((fc: FormControl) => {
            this.saveValue(fc, formElement.formData.key)
          }));
      });
    })
  }


  getFormElementByKey(key: string): TAnyFormElement {
    return this.flattenedFormElements()[key];
  }

  saveValue(fc: FormControl, formKey: string | number): void {
    // find the form group that contains the form element
    const formGroup = this.data().formGroups.find((fg: IFormGroup): boolean =>
      fg.elements.some((el: TAnyFormElement) => el.key === formKey)
    );

    const storedElements: TAnyFormElement[] = Object.values(this.controlFormObjects);

    // check if there are any changes and cancel otherwise (compared against the last
    // successfully saved values)
    if (storedElements.every((formEl: TAnyFormElement): boolean => formEl.value === this.form().get(formEl.key).value)) return;

    // map the form control to the initial db object and set the value to the input value
    // Send a copy with the new value instead of mutating the stored element: the stored
    // value must only advance on a successful save (see updateForm), otherwise a failed
    // save would block retrying the same value via the no-change guard above.
    const elements: TAnyFormElement[] = storedElements.map((el: TAnyFormElement): TAnyFormElement =>
      el.key === formKey ? {...el, value: fc.value} : el
    );

    // emit the form change event with the updated form elements to the base component
    // Will call updateForm method to update the form with the new values
    this.formChange$.next({elements, formGroup, changedElement: formKey});
  }

  // Update the form with the new form group (called with the values of the save response)
  override updateForm(value: any): void {
    this.form().patchValue(value, {emitEvent: false});
    Object.values(this.controlFormObjects).forEach((el: TAnyFormElement): void => {
      if (value[el.key] !== undefined) {
        el.value = value[el.key];
      }
    });
  }
}
